/**
 * The Shift Roster's people list for one day: everyone on the plant's crews,
 * who is placed on a job that day, who is on shift and free (available), who
 * is out, and who is off. People placed from elsewhere (a sister plant) are
 * listed too.
 */
import { format, parseISO } from "date-fns";

import { HomeCrewMember } from "../../../../../services/roster-assignments-api";

export type PersonDayState = "available" | "allocated" | "out" | "off";

export interface PersonOnDay {
	workerId: string;
	workerName: string;
	/** Home line and crew ("Glatt", "12h day"); null for someone from elsewhere. */
	home: string | null;
	shiftLabel: string | null;
	/** Their own shift that day (plant-local ISO), when they have one. */
	shift: { shiftId: string; start: string; end: string } | null;
	state: PersonDayState;
	/** What they are on that day: line and hours, in time order. */
	work: { workstation: string; start: string; end: string }[];
}

interface Block {
	workerId: string;
	workerName: string;
	workstation?: string | null;
	startTime: string;
	endTime: string;
}

const ORDER: PersonDayState[] = ["available", "allocated", "out", "off"];
const dayOf = (iso: string) => iso.slice(0, 10);
const ms = (iso: string) => parseISO(iso).getTime();

/**
 * Everyone for the day, available first. Someone is allocated when a block of
 * theirs (at any plant) overlaps their shift that day (or starts that day when
 * they have no shift); available when on shift, free and not out. People from
 * outside the crews are listed when they work on one of `lines` that day.
 */
export function peopleOnDay(
	crews: HomeCrewMember[],
	blocks: Block[],
	day: Date,
	isOut: (workerId: string, shiftId: string | null, dayIso: string) => boolean,
	lines?: Set<string>
): PersonOnDay[] {
	const dayIso = format(day, "yyyy-MM-dd");
	const byWorker = new Map<string, Block[]>();
	blocks.forEach((b) => byWorker.set(String(b.workerId), [...(byWorker.get(String(b.workerId)) ?? []), b]));

	const workOf = (workerId: string, shift: PersonOnDay["shift"]) =>
		(byWorker.get(workerId) ?? [])
			.filter((b) => (shift ? ms(b.startTime) < ms(shift.end) && ms(b.endTime) > ms(shift.start) : dayOf(b.startTime) === dayIso))
			.sort((a, b) => ms(a.startTime) - ms(b.startTime))
			.map((b) => ({ workstation: b.workstation ?? "", start: b.startTime, end: b.endTime }));

	const seen = new Set<string>();
	const people: PersonOnDay[] = crews.map((crew) => {
		seen.add(crew.workerId);
		const shift = (crew.shifts ?? []).find((s) => dayOf(s.start) === dayIso) ?? null;
		const work = workOf(crew.workerId, shift);
		const out = isOut(crew.workerId, shift?.shiftId ?? null, dayIso);
		const state: PersonDayState = work.length > 0 ? "allocated" : out ? "out" : shift ? "available" : "off";
		return { workerId: crew.workerId, workerName: crew.workerName, home: crew.workstation, shiftLabel: crew.shiftLabel, shift, state, work };
	});
	// People on the day's roster who are not on this plant's crews.
	byWorker.forEach((list, workerId) => {
		if (seen.has(workerId)) return;
		const work = workOf(workerId, null);
		if (work.length === 0 || (lines && !work.some((w) => lines.has(w.workstation.toLowerCase())))) return;
		people.push({ workerId, workerName: list[0].workerName, home: null, shiftLabel: null, shift: null, state: "allocated", work });
	});
	return people.sort((a, b) => ORDER.indexOf(a.state) - ORDER.indexOf(b.state) || a.workerName.localeCompare(b.workerName));
}
